import React from "react";
import { useParams, Navigate, Link } from "react-router-dom";
import { seoPageContent } from "@/data/seoPages";
import useSEO from "@/hooks/useSEO";
import { SITE_URL } from "@/lib/siteUrl";
import Faq, { faqJsonLd } from "@/components/Faq";
import Breadcrumbs from "@/components/Breadcrumbs";
import { Hero, InternalLinks, Section } from "@/components/seo/SeoLayout";

const SeoLandingPage = ({ slug }) => {
  const data = seoPageContent[slug];
  useSEO({
    title: data?.title,
    description: data?.description,
    keywords: data?.keywords,
    jsonLd: data ? [
      faqJsonLd(data.faq),
      {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "Home", item: SITE_URL + "/" },
          { "@type": "ListItem", position: 2, name: data.kicker, item: SITE_URL + data.slug },
        ],
      },
    ] : null,
  });
  if (!data) return <Navigate to="/404" replace />;

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8 pb-24">
      <Breadcrumbs items={[{ label: data.kicker }]} />
      <Hero
        kicker={data.kicker}
        title={data.h1}
        subtitle={data.lede}
        primary={{ to: "/products", label: "Explore products" }}
        secondary={{ to: "/methodology", label: "Read methodology" }}
      />

      {slug === "sustainability-score" && (
        <>
          <Section id="how-it-works" title="How the score is calculated" kicker="7-metric analysis">
            <p>Ecowallet's AI reviews publicly available information about a product, then scores <strong>seven measurable dimensions</strong> — each on a 0–100 scale:</p>
            <ol className="list-decimal pl-6 space-y-1.5">
              <li><strong>Carbon Footprint</strong> — production and lifecycle CO₂ impact.</li>
              <li><strong>Packaging</strong> — minimal, recyclable, plastic-free.</li>
              <li><strong>Materials</strong> — recycled, natural, renewable inputs.</li>
              <li><strong>Recyclability</strong> — end-of-life recyclability or biodegradability.</li>
              <li><strong>Manufacturing</strong> — ethical labour and energy-efficient factories.</li>
              <li><strong>Shipping</strong> — local sourcing and low-emission transport.</li>
              <li><strong>Certifications</strong> — recognised third-party certifications (GOTS, USDA Organic, Fair Trade, B Corp, etc.).</li>
            </ol>
            <p>These are combined into a weighted overall score with a confidence rating (low / medium / high) and per-metric reasoning that we publish on every product page.</p>
          </Section>
          <Section id="how-to-read" title="How to read a Sustainability Score">
            <ul className="list-disc pl-6 space-y-1.5">
              <li><strong>80–100</strong>: Strong performance across most dimensions — a genuinely sustainable choice.</li>
              <li><strong>65–79</strong>: Meaningfully better than conventional alternatives.</li>
              <li><strong>55–64</strong>: Middle of the pack — likely a greener alternative exists.</li>
              <li><strong>Below 55</strong>: Significant environmental concerns; check the alternatives section.</li>
            </ul>
          </Section>
        </>
      )}

      {slug === "sustainable-products" && (
        <>
          <Section id="find-sustainable-products" title="How to find sustainable products online">
            <p>Ecowallet's product finder makes it easy to <strong>discover sustainable products online</strong> across categories. Every listing carries an AI Sustainability Score so you can make eco-friendly decisions without endless research.</p>
            <ol className="list-decimal pl-6 space-y-1.5">
              <li>Browse by category — Fashion, Beauty, Home &amp; Kitchen, Electronics, Food &amp; Beverage, Baby &amp; Kids and more.</li>
              <li>Filter by sustainability score, cashback percentage or price.</li>
              <li>Compare products side-by-side using the on-page Sustainability Breakdown.</li>
              <li>Click any product to see a Better Green Alternative if one exists.</li>
            </ol>
          </Section>
          <Section id="compare-sustainability" title="Compare sustainability across products">
            <p>Because every product uses the same 0–100 scoring scale, you can compare unlike products — a bamboo toothbrush vs plastic, a hemp t-shirt vs polyester — on identical criteria.</p>
          </Section>
        </>
      )}

      {slug === "sustainable-alternatives" && (
        <>
          <Section id="what-are-alternatives" title="What is a sustainable alternative?">
            <p>A sustainable alternative is a product that serves the same purpose as a conventional one but with meaningfully lower environmental impact. Examples: <strong>menstrual cups</strong> replacing disposable pads, <strong>bamboo toothbrushes</strong> replacing plastic, <strong>steel straws</strong> replacing single-use plastic.</p>
          </Section>
          <Section id="how-alternatives-are-picked" title="How Ecowallet picks better green alternatives">
            <p>For each product we automatically surface the highest-scoring options in the same category, with clear side-by-side comparison and the specific reasons the alternative is greener — recycled materials, lower carbon footprint, plastic-free packaging or stronger certifications.</p>
          </Section>
        </>
      )}

      {slug === "sustainable-shopping-rewards" && (
        <>
          <Section id="how-rewards-work" title="How sustainable shopping rewards work">
            <p>Every purchase through Ecowallet earns two things: <strong>cashback</strong> (real money back) and <strong>Green Points</strong> (redeemable currency).</p>
            <ul className="list-disc pl-6 space-y-1.5">
              <li>Cashback: 3%–18% depending on the merchant, credited after purchase confirmation.</li>
              <li>Green Points: proportional to the product's Sustainability Score — greener choices earn more.</li>
              <li>Points can be redeemed for gift cards, coupons, tree plantation, or NGO donations.</li>
            </ul>
          </Section>
          <Section id="leaderboard" title="Compete on the sustainability leaderboard">
            <p>Ecowallet gamifies sustainable shopping. Every purchase increases your Green Points, unlocks tier badges (<em>Eco Beginner → Net Zero Champion</em>) and moves you up the community leaderboard.</p>
          </Section>
        </>
      )}

      {slug === "ai-sustainability" && (
        <>
          <Section id="how-ai-works" title="How our AI reasons about sustainability">
            <p>Ecowallet uses <strong>Claude Sonnet 4.6</strong> — a state-of-the-art large language model — as its reasoning engine. For each product we:</p>
            <ol className="list-decimal pl-6 space-y-1.5">
              <li>Scrape the brand's public website and the merchant product page.</li>
              <li>Collect declared certifications and materials.</li>
              <li>Pass everything to the AI with a strict prompt that requires evidence-backed reasoning on all seven metrics.</li>
              <li>Parse the JSON response and publish it transparently, including the exact prompt used.</li>
            </ol>
          </Section>
          <Section id="transparency" title="Transparency by default">
            <p>Every product page includes an "AI Methodology" tab showing sources analysed, the exact system + user prompt sent to the AI, per-metric reasoning, generated timestamp and a confidence rating. You can contest any score.</p>
          </Section>
        </>
      )}

      {slug === "greenwashing" && (
        <>
          <Section id="what-is-greenwashing" title="What is greenwashing?">
            <p>Greenwashing is when a company markets a product as environmentally friendly without evidence to back the claim. It ranges from vague words ("green", "natural", "clean") to misleading imagery to unverified carbon-neutral claims.</p>
          </Section>
          <Section id="ecowallet-detects" title="How Ecowallet detects greenwashing">
            <ul className="list-disc pl-6 space-y-1.5">
              <li>Weighs certifications and verifiable evidence far more than marketing language.</li>
              <li>Lowers the Sustainability Score whenever a claim cannot be substantiated in the sources analysed.</li>
              <li>Publishes the exact reasoning per metric so you can see which claims held up.</li>
            </ul>
          </Section>
          <Section id="examples" title="Common greenwashing examples">
            <ul className="list-disc pl-6 space-y-1.5">
              <li>"Natural" with no certification.</li>
              <li>Recyclable symbols printed on non-recyclable multilayer packaging.</li>
              <li>Carbon-neutral claims without verified offsets or lifecycle data.</li>
              <li>"Made from recycled materials" without a percentage.</li>
            </ul>
          </Section>
        </>
      )}

      {slug === "methodology" && (
        <>
          <Section id="data" title="Data we analyse">
            <p>For each product, Ecowallet's engine collects:</p>
            <ul className="list-disc pl-6 space-y-1.5">
              <li>The product name, description and declared certifications.</li>
              <li>The brand's public website (scraped landing content).</li>
              <li>The merchant product page (Amazon, Flipkart, Myntra, Nykaa, etc.).</li>
              <li>Any publicly reported materials or manufacturing information.</li>
            </ul>
          </Section>
          <Section id="metrics" title="Seven scoring dimensions">
            <p>See the seven metrics used in every score, with each metric explained in the <Link to="/sustainability-score" className="text-primary hover:underline">Sustainability Score page</Link>.</p>
          </Section>
          <Section id="evidence-vs-claims" title="Verified evidence vs company claims">
            <p>We distinguish clearly between:</p>
            <ul className="list-disc pl-6 space-y-1.5">
              <li><strong>Verified / publicly reported</strong> — certifications, published reports.</li>
              <li><strong>Company claims</strong> — marketing statements without third-party verification (discounted).</li>
              <li><strong>Third-party information</strong> — journalism, industry reports.</li>
              <li><strong>AI-generated inference</strong> — clearly labelled as such and used only where evidence is thin.</li>
            </ul>
            <p><strong>We never claim the AI Sustainability Score is 100% accurate.</strong> Accuracy is a function of evidence quality — and we publish the evidence so you can judge for yourself.</p>
          </Section>
          <Section id="contest" title="Contest a score">
            <p>Every product page has a <em>Contest this score</em> button. Provide evidence, and our team reviews within seven days. Verified contests trigger an automatic re-score.</p>
          </Section>
        </>
      )}

      {/* FAQ */}
      <section className="mt-16" id="faq">
        <h2 className="text-3xl font-bold tracking-tight mb-6">Frequently Asked Questions</h2>
        <Faq items={data.faq} testId={`faq-${slug}`} />
      </section>

      <InternalLinks links={data.relatedLinks} />
    </div>
  );
};

export default SeoLandingPage;
